// src/pages/cadastros/Defeitos/EditarDefeito.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

function EditarDefeito() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [nome, setNome] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [empresaId, setEmpresaId] = useState("");
  const [setorId, setSetorId] = useState("");

  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [listaSetores, setListaSetores] = useState([]);
  const [listaMaquinas, setListaMaquinas] = useState([]);
  const [maquinasSelecionadas, setMaquinasSelecionadas] = useState([]);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    async function carregarBase() {
      if (!usuarioLogado?.grupoId) {
        setErro(
          "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
        );
        setCarregando(false);
        return;
      }

      try {
        const empQ = query(
          collection(db, "empresas"),
          where("grupoId", "==", usuarioLogado.grupoId)
        );
        const snapEmp = await getDocs(empQ);
        const listaEmp = snapEmp.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((e) => e.grupoId === usuarioLogado.grupoId);
        setListaEmpresas(listaEmp);

        const setQ = query(
          collection(db, "setores"),
          where("grupoId", "==", usuarioLogado.grupoId)
        );
        const snapSet = await getDocs(setQ);
        const listaSet = snapSet.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setListaSetores(listaSet);

        const maqQ = query(
          collection(db, "maquinas"),
          where("grupoId", "==", usuarioLogado.grupoId)
        );
        const snapMaq = await getDocs(maqQ);
        const listaMaq = snapMaq.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((m) => m.grupoId === usuarioLogado.grupoId);
        setListaMaquinas(listaMaq);

        const refDef = doc(db, "defeitos", id);
        const snapDef = await getDoc(refDef);
        if (!snapDef.exists()) {
          setErro("Defeito não encontrado.");
          setCarregando(false);
          return;
        }

        const dados = snapDef.data();
        setNome(dados.nome || "");
        setObservacoes(dados.observacoes || "");
        setEmpresaId(dados.globalEmpresa ? "__GLOBAL__" : dados.empresaId || "");
        setSetorId(
          dados.todosSetoresEmpresa || dados.globalEmpresa
            ? "__TODOS__"
            : dados.setorId || ""
        );
        setMaquinasSelecionadas(dados.maquinasIds || []);
      } catch (e) {
        console.error("Erro ao carregar dados do defeito:", e);
        setErro("Erro ao carregar dados do defeito.");
      } finally {
        setCarregando(false);
      }
    }

    carregarBase();
  }, [usuarioLogado, id]);

  const setoresFiltrados = useMemo(() => {
    if (!empresaId || empresaId === "__GLOBAL__") return listaSetores;
    return listaSetores.filter((s) => s.empresaId === empresaId);
  }, [listaSetores, empresaId]);

  const maquinasFiltradas = useMemo(() => {
    let base = [...listaMaquinas];

    if (empresaId && empresaId !== "__GLOBAL__") {
      base = base.filter((m) => m.empresaId === empresaId);
    }

    if (setorId && setorId !== "__TODOS__") {
      base = base.filter((m) => m.setorId === setorId);
    }

    return base.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  }, [listaMaquinas, empresaId, setorId]);

  function toggleMaquina(maquinaId) {
    setMaquinasSelecionadas((prev) =>
      prev.includes(maquinaId)
        ? prev.filter((id) => id !== maquinaId)
        : [...prev, maquinaId]
    );
  }

  function selecionarTodas() {
    const todoIds = maquinasFiltradas.map((m) => m.id);
    setMaquinasSelecionadas(todoIds);
  }

  function limparSelecao() {
    setMaquinasSelecionadas([]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do defeito.");
      return;
    }

    if (!empresaId) {
      setErro("Selecione uma empresa ou Global.");
      return;
    }

    if (maquinasSelecionadas.length === 0) {
      setErro("Selecione ao menos uma máquina para o defeito.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;

    const globalEmpresa = empresaId === "__GLOBAL__";
    const empresaSelecionada = listaEmpresas.find((e) => e.id === empresaId) || null;
    const empresaNome = globalEmpresa
      ? "Global (todas as empresas do grupo)"
      : empresaSelecionada?.nome || null;

    const todosSetoresEmpresa = setorId === "__TODOS__" || globalEmpresa;
    const setorSelecionado = setoresFiltrados.find((s) => s.id === setorId) || null;
    const setorNome = todosSetoresEmpresa
      ? "Todos os setores"
      : setorSelecionado?.nome || null;

    const maquinasDocs = listaMaquinas.filter((m) =>
      maquinasSelecionadas.includes(m.id)
    );
    const maquinasNomes = maquinasDocs.map((m) => m.nome || "(sem nome)");

    setSalvando(true);

    try {
      const ref = doc(db, "defeitos", id);
      await updateDoc(ref, {
        nome: nome.trim(),
        observacoes: observacoes.trim() || null,

        grupoId,
        grupoNome,

        empresaId: globalEmpresa ? null : empresaId,
        empresaNome,
        globalEmpresa,

        setorId: todosSetoresEmpresa ? null : setorId,
        setorNome,
        todosSetoresEmpresa,

        maquinasIds: maquinasSelecionadas,
        maquinasNomes,

        ativo: true,

        updatedAt: new Date(),
      });

      setSucesso("Defeito atualizado com sucesso.");
    } catch (e) {
      console.error("Erro ao atualizar defeito:", e);
      setErro("Erro ao atualizar defeito. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  function voltarLista() {
    navigate("/cadastros/defeitos");
  }

  if (carregando) {
    return (
      <LayoutDesktop titulo="Editar defeito">
        <section style={{ width: "100%" }}>
          <div className="pc-card">
            <p>Carregando dados do defeito...</p>
          </div>
        </section>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop titulo="Editar defeito">
      <section style={{ width: "100%" }}>
        <div className="pc-card">
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0 }}>Editar defeito</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                Ajuste as informações do defeito e das máquinas associadas.
              </p>
            </div>

            <button type="button" className="pc-btn-secundario" onClick={voltarLista}>
              Voltar
            </button>
          </header>

          {erro && (
            <div className="pc-alert pc-alert-erro">
              {erro}
            </div>
          )}

          {sucesso && (
            <div className="pc-alert pc-alert-sucesso">
              {sucesso}
            </div>
          )}

          <form onSubmit={handleSubmit} className="pc-form">
            <div className="pc-form-group">
              <label>Nome do defeito</label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Vazamento de óleo"
                required
              />
            </div>

            <div className="pc-form-row">
              <div className="pc-form-group">
                <label>Empresa</label>
                <select
                  value={empresaId}
                  onChange={(e) => {
                    setEmpresaId(e.target.value);
                    setSetorId("");
                  }}
                  required
                >
                  <option value="">Selecione uma empresa</option>
                  <option value="__GLOBAL__">Global (todas as empresas)</option>
                  {listaEmpresas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome || "(sem nome)"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Setor</label>
                <select
                  value={setorId}
                  onChange={(e) => setSetorId(e.target.value)}
                  disabled={!empresaId}
                >
                  {!empresaId ? (
                    <option value="">Selecione a empresa primeiro</option>
                  ) : (
                    <>
                      <option value="__TODOS__">Todos os setores</option>
                      {setoresFiltrados.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nome || "(sem nome)"}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="pc-form-group">
              <label style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  Máquinas 
                  {maquinasSelecionadas.length > 0 && (
                    <span style={{ 
                      marginLeft: 8, 
                      padding: "2px 8px", 
                      borderRadius: 12, 
                      fontSize: 12,
                      background: "var(--cor-acento)",
                      color: "#fff"
                    }}>
                      {maquinasSelecionadas.length} selecionada{maquinasSelecionadas.length > 1 ? "s" : ""}
                    </span>
                  )}
                </span>
                {maquinasFiltradas.length > 0 && (
                  <span style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={selecionarTodas}
                      className="pc-btn-link"
                    >
                      Selecionar todas
                    </button>
                    <button
                      type="button"
                      onClick={limparSelecao}
                      className="pc-btn-link"
                    >
                      Limpar
                    </button>
                  </span>
                )}
              </label>
              
              <div className="pc-checkbox-grid">
                {maquinasFiltradas.length === 0 ? (
                  <p className="pc-empty-message">
                    {!empresaId 
                      ? "Selecione uma empresa para ver as máquinas disponíveis."
                      : "Nenhuma máquina encontrada para os filtros selecionados."}
                  </p>
                ) : (
                  maquinasFiltradas.map((m) => (
                    <label key={m.id} className="pc-checkbox-item">
                      <input
                        type="checkbox"
                        checked={maquinasSelecionadas.includes(m.id)}
                        onChange={() => toggleMaquina(m.id)}
                      />
                      <span className="pc-checkbox-label">
                        <strong>{m.nome || "(sem nome)"}</strong>
                        {m.codigo && <span className="pc-checkbox-code">{m.codigo}</span>}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="pc-form-group">
              <label>Observações (opcional)</label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                placeholder="Informações adicionais sobre o defeito..."
              />
            </div>

            <div className="pc-form-actions">
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={voltarLista}
                disabled={salvando}
              >
                Cancelar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando}>
                {salvando ? "Salvando..." : "Atualizar defeito"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default EditarDefeito;
